import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { fetchCourses } from "@/lib/courses";
import type { Course } from "@/lib/types";
import { useTranslation } from "react-i18next";
import { formatPrice } from "@/lib/utils";
import { useAuth } from "@/components/auth/AuthProvider";
import { useEnrolledCourses } from "@/hooks/useEnrolledCourses";

export default function CoursesPage({ lang = "en" }: { lang?: string }) {
  const { t } = useTranslation();
  // Courses data
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // auth + enrollment
  const { user, loading: authLoading } = useAuth();
  const { enrolledCourses, loading: loadingEnrolled } = useEnrolledCourses({ userId: user?.id, authLoading });
  
  // Map of enrolled course slug to first lesson slug
  const enrollmentMap = useMemo(() => {
    const map: Record<string, { moduleSlug: string | null; lessonSlug: string | null }> = {};
    for (const c of enrolledCourses) {
      const mods = c.modules || [];
      const firstMod = mods.sort((a, b) => a.position - b.position)[0];
      const firstLesson = firstMod?.lessons?.sort((a, b) => a.position - b.position)[0];
      map[c.slug] = { moduleSlug: firstMod ? firstMod.id : null, lessonSlug: firstLesson ? firstLesson.slug : null };
    }
    return map;
  }, [enrolledCourses]);
  
  // Map of all courses' first lesson slug (for free or default linking)
  const firstLessonMap = useMemo(() => {
    const map: Record<string, { moduleSlug: string | null; lessonSlug: string | null }> = {};
    for (const c of courses) {
      const mods = c.modules || [];
      const firstMod = mods.sort((a, b) => a.position - b.position)[0];
      const firstLesson = firstMod?.lessons?.sort((a, b) => a.position - b.position)[0];
      map[c.slug] = { moduleSlug: firstMod ? firstMod.id : null, lessonSlug: firstLesson ? firstLesson.slug : null };
    }
    return map;
  }, [courses]);

  // Effect to fetch basic course data
  useEffect(() => {
    async function loadCourses() {
      setLoading(true);
      const coursesData = await fetchCourses(lang);
      setCourses(coursesData);
      setLoading(false);
    }
    
    loadCourses();
  }, [lang]);

  const filteredCourses = courses.filter(course =>
    course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    course.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-4">{t("courses")}</h1>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <input
            type="text"
            placeholder={t("search_courses")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      </div>

      {loading || loadingEnrolled ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-card border rounded-lg shadow-lg overflow-hidden h-64 animate-pulse">
              <div className="p-6">
                <div className="h-6 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded mb-4"></div>
                <div className="h-4 bg-gray-200 rounded mb-4"></div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredCourses.map((course) => {
            const isEnrolled = user && course.slug ? enrolledCourses.some(ec => ec.slug === course.slug) : false;
            
            return (
              <Link key={course.slug} to={`/${lang === 'en' ? '' : lang + '/'}courses/${course.slug}`} className="block group">
                <div className="bg-card border rounded-lg shadow-lg overflow-hidden h-full flex flex-col hover:shadow-xl transition-shadow duration-300">
                  {course.cover && (
                    <div className="w-full overflow-hidden">
                      <img
                        src={course.cover}
                        alt={course.title}
                        className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300 ease-in-out"
                      />
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-grow">
                    <h2 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors duration-300">{course.title}</h2>
                    <p className="text-muted-foreground text-sm mb-4 line-clamp-3 flex-grow">{course.description}</p>
                  </div>
                  <div className="p-6 pt-0 flex justify-end items-center">
                    {/* price or badge */}
                    {isEnrolled ? (
                      <div className="bg-green-100 px-3 py-2 rounded-full text-sm font-medium text-green-700">Access Granted</div>
                    ) : course.is_free ? (
                      <div className="bg-primary/10 px-3 py-2 rounded-full text-sm font-medium text-primary">{t("free")}</div>
                    ) : course.default_price && course.default_price.unit_amount ? (
                      <div className="bg-primary/10 px-4 py-2 rounded-full text-sm font-medium text-primary price">
                        {formatPrice(course.default_price.unit_amount, course.default_price.currency, lang === 'fr' ? 'fr-FR' : 'en-US')}
                      </div>
                    ) : (
                      <div className="bg-gray-200 px-3 py-2 rounded-full text-sm font-medium text-gray-500">Price Info N/A</div>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
